import styled from '@emotion/styled';

import { Icon } from '@travelnest/hui';

import { formatShortDate } from '@/lib/dates';
import { colors, focusRing, font, radii, spacing } from '@/lib/theme';
import { useCurrentLocale, useI18n } from '@/locales';

const Field = styled.button`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 2px;
	width: 100%;
	min-width: 0;
	padding: ${spacing.space20} ${spacing.space30};
	background: ${colors.LM_NU0};
	border: 1px solid ${colors.LM_NU40};
	border-radius: ${radii.radii30};
	text-align: left;
	cursor: pointer;
	transition:
		border-color 150ms ease-out,
		background-color 150ms ease-out;

	&:hover {
		border-color: ${colors.LM_NU60};
		background: ${colors.LM_NU10};
	}
	&:focus-visible {
		${focusRing}
	}
`;

const Label = styled.span`
	${font('IM80')}
	text-transform: uppercase;
	letter-spacing: 0.4px;
	color: ${colors.typography.body};
`;

const Value = styled.span<{ isPlaceholder?: boolean }>`
	${font('IR100')}
	color: ${({ isPlaceholder }) => (isPlaceholder ? colors.LM_NU60 : colors.typography.heading)};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 100%;
`;

const DateRow = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: ${spacing.space20};
`;

const GuestsValue = styled.span`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;

	svg {
		color: ${colors.LM_NU60};
	}
`;

interface DateFieldsProps {
	startDate?: string;
	endDate?: string;
	onClick: () => void;
}

/** The two tappable date fields on the booking card. Either opens the calendar. */
export const DateFields = ({
	startDate,
	endDate,
	onClick,
}: DateFieldsProps) => {
	const t = useI18n();
	const locale = useCurrentLocale();
	return (
		<DateRow>
			<Field type="button" onClick={onClick} data-testid="check-in-field">
				<Label>{t('booking.checkIn')}</Label>
				<Value isPlaceholder={!startDate}>
					{startDate
						? formatShortDate(startDate, locale)
						: t('booking.addCheckIn')}
				</Value>
			</Field>
			<Field
				type="button"
				onClick={onClick}
				data-testid="check-out-field"
			>
				<Label>{t('booking.checkOut')}</Label>
				<Value isPlaceholder={!endDate}>
					{endDate
						? formatShortDate(endDate, locale)
						: t('booking.addCheckOut')}
				</Value>
			</Field>
		</DateRow>
	);
};

interface GuestsFieldProps {
	adults: number;
	childCount: number;
	onClick: () => void;
}

/** The tappable guests field on the booking card. Opens the guest picker. */
export const GuestsField = ({
	adults,
	childCount,
	onClick,
}: GuestsFieldProps) => {
	const t = useI18n();
	const total = adults + childCount;
	return (
		<Field type="button" onClick={onClick} data-testid="guests-field">
			<Label>{t('booking.guests')}</Label>
			<GuestsValue>
				<Value>
					{total === 1
						? t('booking.summary.guest')
						: t('booking.summary.guests', { count: total })}
				</Value>
				<Icon name="ChevronDown" size={24} />
			</GuestsValue>
		</Field>
	);
};
